import { useTranslation } from "@pancakeswap/localization";
import { Currency, Price } from "@pancakeswap/swap-sdk-core";
import { FeeAmount } from "@pancakeswap/v3-sdk";
import { styled, useTheme } from "styled-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "d3";
import { captureMessage } from "@sentry/nextjs";
import { saturate } from "polished";
import { AutoColumn, BunnyKnownPlaceholder, ChartDisableIcon, FlexGap, LineGraphIcon } from "@pancakeswap/uikit";

import { Bound, ChartEntry, TickDataRaw, ZOOM_LEVELS, ZoomLevels } from "./types";
import { InfoBox } from "./InfoBox";
import Loader from "./Loader";
import { Chart } from "./Chart";

const StyledLogo = styled.img`
  height: 180px;
  margin: 1em;
  filter: grayscale(0.8);
  opacity: 0.5;
`

export function LiquidityChartRangeInput({
  currencyA,
  currencyB,
  feeAmount,
  ticksAtLimit = {},
  price,
  priceHigh24H,
  priceLow24H,
  priceLower,
  priceUpper,
  onBothRangeInput = () => {
    // default
  },
  onLeftRangeInput = () => {
    // default
  },
  onRightRangeInput = () => {
    // default
  },
  interactive = false,
  isLoading,
  error,
  zoomLevel,
  formattedData,
  maxLiquidity,
  dimensions
}: {
  tickCurrent?: number;
  liquidity?: bigint;
  isLoading?: boolean;
  error?: Error;
  currencyA?: Currency;
  currencyB?: Currency;
  feeAmount?: FeeAmount;
  ticks?: TickDataRaw[];
  ticksAtLimit?: { [bound in Bound]?: boolean };
  price?: number;
  priceHigh24H?: number;
  priceLow24H?: number;
  priceLower?: Price<Currency, Currency>;
  priceUpper?: Price<Currency, Currency>;
  onLeftRangeInput?: (typedValue: string) => void;
  onRightRangeInput?: (typedValue: string) => void;
  onBothRangeInput?: (leftTypedValue: string, rightTypedValue: string) => void;
  interactive?: boolean;
  zoomLevel?: ZoomLevels;
  formattedData: ChartEntry[];
  maxLiquidity?: number;
  dimensions?: any
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  const [latestZoomLevel, setLatestZoomLevel] = useState<ZoomLevels>()

  useEffect(() => {
    if (zoomLevel)
      setLatestZoomLevel(zoomLevel)
  }, [zoomLevel])

  // Get token color
  const tokenAColor = "#B7ADD1";
  const tokenBColor = "#B7ADD1";

  const isSorted = useMemo(
    () => currencyA && currencyB && currencyA?.wrapped.sortsBefore(currencyB?.wrapped),
    [currencyA, currencyB]
  );

  const brushDomain: [number, number] | undefined = useMemo(() => {
    const leftPrice = isSorted ? priceLower : priceUpper?.invert();
    const rightPrice = isSorted ? priceUpper : priceLower?.invert();

    return leftPrice && rightPrice
      ? [parseFloat(leftPrice?.toSignificant(18)), parseFloat(rightPrice?.toSignificant(18))]
      : undefined;
  }, [isSorted, priceLower, priceUpper]);

  const onBrushDomainChangeEnded = useCallback(
    (domain: [number, number], mode: string | undefined) => {
      const [leftPrice, rightPrice] = brushDomain || [];

      let leftRangeValue = Number(domain[0]);
      const rightRangeValue = Number(domain[1]);

      if (leftRangeValue <= 0) {
        leftRangeValue = 1 / 10 ** 6;
      }

      const updateLeft =
        (!ticksAtLimit[isSorted ? Bound.LOWER : Bound.UPPER] || mode === "handle" || mode === "reset") &&
        leftRangeValue > 0 &&
        leftRangeValue !== leftPrice;

      const updateRight =
        (!ticksAtLimit[isSorted ? Bound.UPPER : Bound.LOWER] || mode === "reset") &&
        rightRangeValue > 0 &&
        rightRangeValue < 1e35 &&
        rightRangeValue !== rightPrice;

      if (updateLeft && updateRight) {
        const parsedLeftRangeValue = parseFloat(leftRangeValue.toFixed(18));
        const parsedRightRangeValue = parseFloat(rightRangeValue.toFixed(18));
        if (parsedLeftRangeValue > 0 && parsedRightRangeValue > 0 && parsedLeftRangeValue < parsedRightRangeValue) {
          onBothRangeInput?.(leftRangeValue.toFixed(18), rightRangeValue.toFixed(18));
        }
      } else if (updateLeft) {
        onLeftRangeInput?.(leftRangeValue.toFixed(18));
      } else if (updateRight) {
        onRightRangeInput?.(rightRangeValue.toFixed(18));
      }
    },
    [isSorted, onBothRangeInput, onLeftRangeInput, onRightRangeInput, ticksAtLimit, brushDomain]
  );

  const brushLabelValue = useCallback(
    (d: "w" | "e", x: number) => {
      if (!price) return "";

      if (d === "w" && ticksAtLimit[isSorted ? Bound.LOWER : Bound.UPPER]) return "0";
      if (d === "e" && ticksAtLimit[isSorted ? Bound.UPPER : Bound.LOWER]) return "∞";

      const percent = (x < price ? -1 : 1) * ((Math.max(x, price) - Math.min(x, price)) / price) * 100;

      return price ? `${format(Math.abs(percent) > 1 ? ".2~s" : ".2~f")(percent)}%` : "";
    },
    [isSorted, price, ticksAtLimit]
  );

  if (error) {
    captureMessage(error.toString(), "log");
  }

  const isUninitialized = !currencyA || !currencyB // || (formattedData === undefined && !isLoading);

  return (
    <AutoColumn gap="md" style={{ width: "100%", marginBottom: "16px" }}>
      {isUninitialized ? (
        <InfoBox icon={<Loader size="40px" stroke={theme.colors.text} />} />
      ) : isLoading ? (
        <InfoBox icon={<Loader size="40px" stroke={theme.colors.text} />} />
      ) : error ? (
        <InfoBox message={t("Liquidity data not available.")} icon={<ChartDisableIcon width="40px" />} />
      ) : !formattedData || formattedData.length === 0 || !price ? (
        <InfoBox message={t("There is no liquidity data.")} icon={<LineGraphIcon width="40px" />} />
      ) : (
        <FlexGap gap="8px" flexDirection={["column-reverse", "column-reverse", "row-reverse"]}>
          <Chart
            key={`${feeAmount ?? FeeAmount.MEDIUM}`}
            data={{ series: formattedData, max: maxLiquidity, current: price ?? 0, high: priceHigh24H, low: priceLow24H }}
            dimensions={{ width: 500, height: 240, ...dimensions }}
            margins={{ top: interactive ? 60 : 20, right: 0, bottom: 30, left: 0, ...dimensions }}
            styles={{
              area: {
                selection: "red",
              },
              brush: {
                handle: {
                  west: saturate(0.1, tokenAColor) ?? theme.colors.text,
                  east: saturate(0.1, tokenBColor) ?? theme.colors.text,
                },
              },
            }}
            interactive={interactive}
            brushLabels={brushLabelValue}
            brushDomain={brushDomain}
            onBrushDomainChange={onBrushDomainChangeEnded}
            zoomLevels={zoomLevel ?? latestZoomLevel ?? ZOOM_LEVELS[feeAmount ?? FeeAmount.MEDIUM]}
            ticksAtLimit={ticksAtLimit}
          />
        </FlexGap>
      )}
    </AutoColumn>
  );
}
